import { useEffect, useMemo, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { paletteColor } from "../lib/palette";
import { useSearchParams } from "react-router-dom";
import { Archive, ArchiveRestore, ChevronDown, ChevronLeft, ChevronRight, Inbox as InboxIcon, Mail, MailOpen, PenLine, RefreshCw, Reply, Send, Square, SquareCheck, Star, Trash2, User, X } from "lucide-react";
import { useStore } from "../lib/store";
import type { CommunicationMessage, CommunicationThread } from "../features/communication/contracts";
import {
  MAIL_FOLDER_LABEL,
  filterMailThreads,
  isMailFolder,
  lastMessageOf,
  mailFolderCounts,
  mailFullStamp,
  mailStamp,
  threadMessages,
  type MailFolder,
} from "../features/communication/inbox-view";
import { priorityLabel } from "../features/leads/list";
import { PageHead } from "../ui/PageHead";
import { SideNav } from "../ui/SideNav";
import { Button } from "../ui/Button";
import { IconButton } from "../ui/IconButton";
import { SearchInput } from "../ui/SearchInput";
import { EmptyState } from "../ui/EmptyState";
import { Avatar } from "../ui/Avatar";
import { Dialog } from "../ui/Dialog";
import { Menu } from "../ui/Menu";
import { Field, TextInput } from "../ui/Field";
import { Select } from "../ui/Select";
import { HelpPopover } from "../ui/HelpPopover";
import { LeadQuickPeek } from "./ListaPage";
import { CosmosAiIcon } from "../ui/CosmosAiIcon";
import { localSuggestion } from "../features/communication/local-suggestion";
import "./inbox.css";

/**
 * Inbox — a caixa de e-mail do protótipo.
 *
 * A diferença para Mensagens não é estética: e-mail tem pastas, seleção em
 * massa, favoritos e uma cadeia de envio própria — rascunho guardado, fila e
 * cópia em Enviados (§27.3). Por isso a superfície é outra, sobre os mesmos
 * comandos de comunicação, e a entrega continua `simulated` enquanto não houver
 * provedor, permissão e política.
 */

const DEMO_NOTE =
  "Caixa de e-mail sem conexão real. Pastas, favoritos, rascunhos e envios são demonstrações locais; nenhuma mensagem sai deste ambiente.";

const FOLDER_ICON: Record<MailFolder, ReactNode> = {
  inbox: <InboxIcon aria-hidden="true" />,
  unread: <Mail aria-hidden="true" />,
  sent: <Send aria-hidden="true" />,
  drafts: <PenLine aria-hidden="true" />,
  starred: <Star aria-hidden="true" />,
  archived: <Archive aria-hidden="true" />,
  trash: <Trash2 aria-hidden="true" />,
};

const PAGE_SIZE = 50;

export function InboxPage() {
  /** Lead do acesso rápido aberto por "Ver contato"; o perfil é outra ação. */
  const [peekLeadId, setPeekLeadId] = useState<string | null>(null);
  const store = useStore();
  const [params, setParams] = useSearchParams();
  const [selection, setSelection] = useState<string[]>([]);
  const [composeOpen, setComposeOpen] = useState(false);

  const threads = useMemo(
    () => (store.communicationThreads ?? []).filter((thread) => thread.channel === "email"),
    [store.communicationThreads],
  );
  const messages = useMemo(() => store.communicationMessages ?? [], [store.communicationMessages]);

  const folderParam = params.get("pasta");
  const folder: MailFolder = isMailFolder(folderParam) ? folderParam : "inbox";
  const query = params.get("busca") ?? "";
  const openId = params.get("email");
  const page = Math.max(1, Number(params.get("pagina") ?? "1") || 1);

  const counts = useMemo(() => mailFolderCounts(threads, messages), [threads, messages]);
  const rows = useMemo(
    () => filterMailThreads(threads, messages, folder, query),
    [threads, messages, folder, query],
  );
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const selected = openId ? threads.find((thread) => thread.id === openId) : undefined;
  const openIndex = selected ? rows.findIndex((thread) => thread.id === selected.id) : -1;

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    // Trocar de pasta ou de busca recomeça a paginação e fecha a leitura.
    if (key === "pasta" || key === "busca") {
      next.delete("pagina");
      next.delete("email");
    }
    setParams(next, { replace: true });
  };

  // Uma seleção só vale para a lista que está na tela.
  useEffect(() => setSelection([]), [folder, query, page]);

  const toggleSelection = (id: string) =>
    setSelection((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );

  const bulk = async (transition: "mark_read" | "mark_unread" | "resolve" | "trash") => {
    for (const id of selection) {
      await store.transitionEmailThread({ threadId: id, transition });
    }
    setSelection([]);
  };

  const open = (thread: CommunicationThread) => {
    const next = new URLSearchParams(params);
    next.set("email", thread.id);
    setParams(next, { replace: true });
  };

  const close = () => {
    const next = new URLSearchParams(params);
    next.delete("email");
    setParams(next, { replace: true });
  };

  const step = (delta: number) => {
    const target = rows[openIndex + delta];
    if (target) open(target);
  };

  return (
    <div className="cx-inbox cx-commpage">
      <PageHead
        title="Inbox"
        sub={
          <>
            <strong>
              {counts.unread} {counts.unread === 1 ? "e-mail não lido" : "e-mails não lidos"}
            </strong>
            . Acompanhe suas conversas por e-mail.
          </>
        }
        actions={
          <>
            <span className="cx-comm-demo">
              <i aria-hidden="true" />
              Demonstração local
              <HelpPopover
                about="a caixa de e-mail"
                label="Sobre a caixa"
                align="end"
                items={[{ term: "Demonstração local", description: DEMO_NOTE }]}
              />
            </span>
            <Button variant="primary" icon={<PenLine aria-hidden="true" />} onClick={() => setComposeOpen(true)}>
              Novo e-mail
            </Button>
          </>
        }
      />

      <div className="cx-commpage-workspace">
        <SideNav
          className="cx-commpage-nav"
          label="Pastas da caixa de e-mail"
          value={folder}
          onChange={(id) => setParam("pasta", id === "inbox" ? null : id)}
          groups={[
            {
              id: "caixa",
              items: (["inbox", "unread", "sent", "drafts", "starred", "archived"] as MailFolder[]).map(
                (id) => ({ id, label: MAIL_FOLDER_LABEL[id], count: counts[id], icon: FOLDER_ICON[id] }),
              ),
            },
            {
              id: "descartados",
              items: [
                { id: "trash", label: MAIL_FOLDER_LABEL.trash, count: counts.trash, icon: FOLDER_ICON.trash },
              ],
            },
          ]}
        />

        <div className="cx-mail-shell cx-commpage-shell">
          {selected ? (
            <Reader
              key={selected.id}
              thread={selected}
              position={openIndex >= 0 ? openIndex + 1 : 1}
              total={rows.length}
              onClose={close}
              onStep={step}
              onOpenLead={(id) => setPeekLeadId(id)}
            />
          ) : (
            <section className="cx-mail-list" aria-label={`Pasta ${MAIL_FOLDER_LABEL[folder]}`}>
              <div className="cx-mail-toolbar">
                <div className="cx-mail-selectwrap">
                  <IconButton
                    label={selection.length === pageRows.length && pageRows.length > 0 ? "Limpar seleção" : "Selecionar tudo"}
                    icon={
                      selection.length > 0 && selection.length === pageRows.length ? (
                        <SquareCheck aria-hidden="true" />
                      ) : (
                        <Square aria-hidden="true" />
                      )
                    }
                    variant="bare"
                    onClick={() =>
                      setSelection((current) =>
                        current.length === pageRows.length ? [] : pageRows.map((thread) => thread.id),
                      )
                    }
                  />
                  <Menu
                    label="Escolher o que selecionar"
                    align="start"
                    trigger={(args) => (
                      <IconButton
                        {...args}
                        label="Escolher o que selecionar"
                        icon={<ChevronDown aria-hidden="true" />}
                        variant="bare"
                        size="compact"
                      />
                    )}
                    sections={[
                      {
                        id: "selecionar",
                        items: [
                          { id: "todos", label: "Todos" },
                          { id: "nenhum", label: "Nenhum" },
                          { id: "lidos", label: "Lidos" },
                          { id: "nao-lidos", label: "Não lidos" },
                          { id: "favoritos", label: "Favoritos" },
                        ],
                      },
                    ]}
                    onSelect={(id) => {
                      if (id === "nenhum") return setSelection([]);
                      if (id === "todos") return setSelection(pageRows.map((thread) => thread.id));
                      const match = (thread: CommunicationThread) =>
                        id === "lidos"
                          ? !thread.unread
                          : id === "nao-lidos"
                            ? thread.unread
                            : Boolean(thread.starredAt);
                      setSelection(pageRows.filter(match).map((thread) => thread.id));
                    }}
                  />
                </div>

                <IconButton
                  label="Sincronizar a caixa"
                  icon={<RefreshCw aria-hidden="true" />}
                  variant="bare"
                  disabled
                  disabledReason="a sincronização exige a conexão real com a caixa, que ainda não existe"
                />

                <SearchInput
                  className="cx-mail-search"
                  value={query}
                  onChange={(value) => setParam("busca", value || null)}
                  label="Pesquisar na caixa"
                  placeholder="Buscar remetente, assunto ou anexo…"
                />

                {selection.length > 0 ? (
                  <div className="cx-mail-selection">
                    <span>
                      {selection.length} {selection.length === 1 ? "selecionado" : "selecionados"}
                    </span>
                    <IconButton
                      label="Marcar como lidos"
                      icon={<MailOpen aria-hidden="true" />}
                      variant="bare"
                      onClick={() => void bulk("mark_read")}
                    />
                    <IconButton
                      label="Marcar como não lidos"
                      icon={<Mail aria-hidden="true" />}
                      variant="bare"
                      onClick={() => void bulk("mark_unread")}
                    />
                    <IconButton
                      label="Arquivar selecionados"
                      icon={<Archive aria-hidden="true" />}
                      variant="bare"
                      onClick={() => void bulk("resolve")}
                    />
                    <IconButton
                      label="Mover selecionados para a lixeira"
                      icon={<Trash2 aria-hidden="true" />}
                      variant="bare"
                      onClick={() => void bulk("trash")}
                    />
                  </div>
                ) : (
                  <div className="cx-mail-pagination">
                    <span>
                      {rows.length === 0
                        ? "0"
                        : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, rows.length)} de ${rows.length}`}
                    </span>
                    <IconButton
                      label="Página anterior"
                      icon={<ChevronLeft aria-hidden="true" />}
                      variant="bare"
                      disabled={page <= 1}
                      onClick={() => setParams(withPage(params, page - 1), { replace: true })}
                    />
                    <IconButton
                      label="Próxima página"
                      icon={<ChevronRight aria-hidden="true" />}
                      variant="bare"
                      disabled={page * PAGE_SIZE >= rows.length}
                      onClick={() => setParams(withPage(params, page + 1), { replace: true })}
                    />
                  </div>
                )}
              </div>

              <div className="cx-mail-rows">
                {pageRows.length === 0 ? (
                  <EmptyState
                    title={query ? "Nenhum e-mail com esse texto" : `Nada em ${MAIL_FOLDER_LABEL[folder]}`}
                    description={
                      query
                        ? "Ajuste a busca para ver os e-mails desta pasta."
                        : folder === "trash"
                          ? "E-mails descartados aparecem aqui e podem ser restaurados."
                          : "Quando chegar correspondência desta pasta, ela aparece aqui."
                    }
                  />
                ) : (
                  pageRows.map((thread) => (
                    <MailRow
                      key={thread.id}
                      thread={thread}
                      checked={selection.includes(thread.id)}
                      onCheck={() => toggleSelection(thread.id)}
                      onOpen={() => open(thread)}
                    />
                  ))
                )}
              </div>
            </section>
          )}
        </div>
      </div>

      {composeOpen && <ComposeDialog onClose={() => setComposeOpen(false)} />}
      {peekLeadId && <LeadQuickPeek leadId={peekLeadId} onClose={() => setPeekLeadId(null)} />}
    </div>
  );
}

function withPage(params: URLSearchParams, page: number) {
  const next = new URLSearchParams(params);
  if (page <= 1) next.delete("pagina");
  else next.set("pagina", String(page));
  return next;
}

/** Linha da lista: quem escreveu, em que etapa está, assunto e prévia. */
function MailRow({
  thread,
  checked,
  onCheck,
  onOpen,
}: {
  thread: CommunicationThread;
  checked: boolean;
  onCheck: () => void;
  onOpen: () => void;
}) {
  const store = useStore();
  const messages = store.communicationMessages ?? [];
  const last = lastMessageOf(messages, thread.id);
  const lead = store.leads.find((item) => item.id === thread.leadId);
  const membership = store.memberships.find((item) => item.leadId === thread.leadId);
  const stage = store.stages.find((item) => item.id === membership?.stageId);
  const starred = Boolean(thread.starredAt);

  return (
    <div className={`cx-mail-row${thread.unread ? " is-unread" : ""}`}>
      <IconButton
        label={checked ? `Desmarcar ${thread.subject}` : `Selecionar ${thread.subject}`}
        icon={checked ? <SquareCheck aria-hidden="true" /> : <Square aria-hidden="true" />}
        variant="bare"
        size="compact"
        onClick={onCheck}
      />
      <IconButton
        label={starred ? `Tirar ${thread.subject} dos favoritos` : `Marcar ${thread.subject} como favorito`}
        icon={<Star aria-hidden="true" />}
        variant="bare"
        size="compact"
        className={starred ? "cx-mail-star is-favorite" : "cx-mail-star"}
        onClick={() =>
          void store.transitionEmailThread({
            threadId: thread.id,
            transition: starred ? "unstar" : "star",
          })
        }
      />
      <button type="button" className="cx-mail-open" onClick={onOpen}>
        <span className="cx-mail-sender">
          <Avatar name={lead?.name ?? thread.counterpartAddress} />
          <span>
            <strong>{lead?.name ?? thread.counterpartAddress}</strong>
            <small>{lead?.company ?? thread.counterpartAddress}</small>
          </span>
        </span>
        <span className="cx-mail-tags">
          {stage && (
            <span className="cx-mail-stage" title={stage.name} style={{ "--cx-tone": paletteColor(stage.color) } as CSSProperties}>
              {stage.name}
            </span>
          )}
          {lead && (
            <span className="cx-lead-priority" style={{ "--cx-tone": priorityTone(lead.priority) } as CSSProperties}>
              <i aria-hidden="true" />
              {priorityLabel(lead.priority)}
            </span>
          )}
        </span>
        <span className="cx-mail-content">
          <strong>{thread.subject}</strong>
          <span className="cx-mail-preview">— {last?.bodyPreview ?? "Sem mensagens"}</span>
        </span>
        <time dateTime={thread.lastMessageAt}>{mailStamp(thread.lastMessageAt)}</time>
      </button>
      <span className="cx-mail-row-actions">
        <IconButton
          label={thread.state === "resolved" ? `Reabrir ${thread.subject}` : `Arquivar ${thread.subject}`}
          icon={thread.state === "resolved" ? <ArchiveRestore aria-hidden="true" /> : <Archive aria-hidden="true" />}
          variant="bare"
          size="compact"
          onClick={() =>
            void store.transitionEmailThread({
              threadId: thread.id,
              transition: thread.state === "resolved" ? "reopen" : "resolve",
            })
          }
        />
        <IconButton
          label={thread.trashedAt ? `Restaurar ${thread.subject}` : `Mover ${thread.subject} para a lixeira`}
          icon={thread.trashedAt ? <ArchiveRestore aria-hidden="true" /> : <Trash2 aria-hidden="true" />}
          variant="bare"
          size="compact"
          onClick={() =>
            void store.transitionEmailThread({
              threadId: thread.id,
              transition: thread.trashedAt ? "restore" : "trash",
            })
          }
        />
      </span>
    </div>
  );
}

function priorityTone(priority: string) {
  return priority === "high" ? "var(--crit)" : priority === "medium" ? "var(--warn)" : "var(--muted)";
}

/** Leitura da conversa: assunto, cartas empilhadas e a resposta. */
function Reader({
  thread,
  position,
  total,
  onClose,
  onStep,
  onOpenLead,
}: {
  thread: CommunicationThread;
  position: number;
  total: number;
  onClose: () => void;
  onStep: (delta: number) => void;
  onOpenLead: (leadId: string) => void;
}) {
  const store = useStore();
  const messages = useMemo(() => store.communicationMessages ?? [], [store.communicationMessages]);
  const lead = store.leads.find((item) => item.id === thread.leadId);
  const membership = store.memberships.find((item) => item.leadId === thread.leadId);
  const stage = store.stages.find((item) => item.id === membership?.stageId);
  const letters = useMemo(() => threadMessages(messages, thread.id), [messages, thread.id]);
  const draft = messages.find((message) => message.threadId === thread.id && message.state === "draft");
  const [replying, setReplying] = useState(false);
  /**
   * Sugestão em revisão, como no `zMailAi` do protótipo: ela aparece acima do
   * rascunho e só entra na conversa se a pessoa aplicar. Nada é enviado por
   * aqui e nenhum provedor é chamado — o texto é montado do próprio contexto.
   */
  /**
   * Sugestão aberta.
   *
   * `pipeline` é a saída do comando de IA — com resumo, revisão humana e o id
   * do resultado, que o feedback precisa. `local` é a redação determinística
   * de `local-suggestion`, usada quando o comando não está disponível; a
   * diferença aparece na tela, porque prometer IA onde ela não rodou seria
   * controle falso (§18).
   */
  const [suggestion, setSuggestion] = useState<
    | {
        origem: "pipeline";
        text: string;
        summary: string;
        requestId: string;
        resultId: string;
        review: boolean;
      }
    | { origem: "local"; text: string }
    | null
  >(null);
  const [sugerindo, setSugerindo] = useState(false);
  const [body, setBody] = useState(draft?.bodyText ?? "");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const starred = Boolean(thread.starredAt);

  // Abrir a carta marca a conversa como lida, como no protótipo.
  useEffect(() => {
    if (!thread.unread) return;
    void store.transitionEmailThread({ threadId: thread.id, transition: "mark_read" });
    // A conversa é a dependência real; o comando não muda entre renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.id]);

  const mailbox = (store.mailboxes ?? []).find((box) => box.channel === "email");

  const saveDraft = async () => {
    if (!mailbox || busy) return null;
    setBusy(true);
    try {
      const result = await store.saveEmailDraft({
        draftId: draft?.id,
        threadId: thread.id,
        mailboxId: mailbox.id,
        to: [thread.counterpartAddress],
        subject: thread.subject.startsWith("Re:") ? thread.subject : `Re: ${thread.subject}`,
        bodyText: body,
      });
      if (!result.ok) {
        setNotice(result.error.message);
        return null;
      }
      return result.value.message.id;
    } finally {
      setBusy(false);
    }
  };

  const send = async (event: FormEvent) => {
    event.preventDefault();
    if (!body.trim() || busy) return;
    const draftId = await saveDraft();
    if (!draftId) return;
    setBusy(true);
    try {
      const result = await store.queueEmailSend(draftId);
      if (!result.ok) {
        setNotice(result.error.message);
        return;
      }
      setBody("");
      setReplying(false);
      setNotice(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="cx-mail-reader" aria-label={`E-mail: ${thread.subject}`}>
      <div className="cx-mail-toolbar">
        <div className="cx-mail-tools">
          <IconButton
            label="Voltar para a lista"
            icon={<ChevronLeft aria-hidden="true" />}
            variant="bare"
            onClick={onClose}
          />
          <IconButton
            label={starred ? "Tirar dos favoritos" : "Marcar como favorito"}
            icon={<Star aria-hidden="true" />}
            variant="bare"
            className={starred ? "cx-mail-star is-favorite" : "cx-mail-star"}
            onClick={() =>
              void store.transitionEmailThread({
                threadId: thread.id,
                transition: starred ? "unstar" : "star",
              })
            }
          />
          <span className="cx-mail-divider" aria-hidden="true" />
          <IconButton
            label="Marcar como não lida"
            icon={<Mail aria-hidden="true" />}
            variant="bare"
            onClick={() => {
              void store.transitionEmailThread({ threadId: thread.id, transition: "mark_unread" });
              onClose();
            }}
          />
          <IconButton
            label={thread.state === "resolved" ? "Reabrir conversa" : "Arquivar conversa"}
            icon={thread.state === "resolved" ? <ArchiveRestore aria-hidden="true" /> : <Archive aria-hidden="true" />}
            variant="bare"
            onClick={() =>
              void store.transitionEmailThread({
                threadId: thread.id,
                transition: thread.state === "resolved" ? "reopen" : "resolve",
              })
            }
          />
          <IconButton
            label={thread.trashedAt ? "Restaurar conversa" : "Mover para a lixeira"}
            icon={thread.trashedAt ? <ArchiveRestore aria-hidden="true" /> : <Trash2 aria-hidden="true" />}
            variant="bare"
            onClick={() =>
              void store.transitionEmailThread({
                threadId: thread.id,
                transition: thread.trashedAt ? "restore" : "trash",
              })
            }
          />
        </div>
        <div className="cx-mail-tools">
          {lead && (
            <Button
              variant="ghost"
              size="compact"
              className="cx-mail-contact"
              icon={<User aria-hidden="true" />}
              onClick={() => onOpenLead(lead.id)}
            >
              Ver contato
            </Button>
          )}
          <span className="cx-mail-position">
            {position} de {total}
          </span>
          <IconButton
            label="E-mail anterior"
            icon={<ChevronLeft aria-hidden="true" />}
            variant="bare"
            disabled={position <= 1}
            onClick={() => onStep(-1)}
          />
          <IconButton
            label="Próximo e-mail"
            icon={<ChevronRight aria-hidden="true" />}
            variant="bare"
            disabled={position >= total}
            onClick={() => onStep(1)}
          />
        </div>
      </div>

      <div className="cx-mail-reading">
        <header className="cx-mail-subject">
          <h2>{thread.subject}</h2>
          <div>
            {stage && (
            <span className="cx-mail-stage" style={{ "--cx-tone": paletteColor(stage.color) } as CSSProperties}>
              {stage.name}
            </span>
          )}
            {lead && (
              <span className="cx-lead-priority" style={{ "--cx-tone": priorityTone(lead.priority) } as CSSProperties}>
                <i aria-hidden="true" />
                {priorityLabel(lead.priority)}
              </span>
            )}
          </div>
        </header>

        <div className="cx-mail-history">
          {letters.map((letter, index) => (
            <Letter key={letter.id} message={letter} thread={thread} open={index === letters.length - 1} />
          ))}

          {notice && (
            <p className="cx-mail-notice" role="status">
              {notice}
            </p>
          )}

          {suggestion && !thread.trashedAt && (
            <section className="cx-mail-ai" aria-label="Sugestão do CosmosAI">
              <header>
                <strong>
                  <span className="cx-mail-ai-spark" aria-hidden="true">
                    <CosmosAiIcon size={14} />
                  </span>
                  Sugestão de resposta{" "}
                  <small>
                    {suggestion.origem === "local"
                      ? "Redação local, sem modelo"
                      : store.aiProviderMode === "local_groq"
                        ? "CosmosAI"
                        : "CosmosAI simulado"}
                  </small>
                </strong>
                <IconButton
                  label="Fechar sugestão"
                  icon={<X aria-hidden="true" />}
                  variant="bare"
                  size="compact"
                  onClick={() => setSuggestion(null)}
                />
              </header>
              {suggestion.origem === "pipeline" && suggestion.summary && (
                <p className="cx-mail-ai-summary">{suggestion.summary}</p>
              )}
              <p>{suggestion.text}</p>
              {suggestion.origem === "pipeline" && suggestion.review && (
                <p className="cx-mail-ai-review" role="status">
                  A sugestão pede revisão humana antes de sair.
                </p>
              )}
              <div className="cx-mail-ai-actions">
                <Button
                  variant="primary"
                  size="compact"
                  onClick={() => {
                    const texto = suggestion.text;
                    if (suggestion.origem === "pipeline")
                      // `accepted`, não `draft_saved`: o texto vai para o
                      // campo de resposta, e o rascunho só passa a existir
                      // quando a resposta é salva ou enviada.
                      void store.recordAiFeedback({
                        requestId: suggestion.requestId,
                        resultId: suggestion.resultId,
                        kind: "accepted",
                      });
                    setBody(texto);
                    setReplying(true);
                    setSuggestion(null);
                  }}
                >
                  Usar no rascunho
                </Button>
                <Button
                  variant="ghost"
                  size="compact"
                  onClick={() => {
                    if (suggestion.origem === "pipeline")
                      void store.recordAiFeedback({
                        requestId: suggestion.requestId,
                        resultId: suggestion.resultId,
                        kind: "discarded",
                      });
                    setSuggestion(null);
                  }}
                >
                  Descartar
                </Button>
              </div>
            </section>
          )}

          {thread.trashedAt ? (
            <p className="cx-mail-closed">
              Conversa na lixeira. Restaure para voltar a responder — o histórico continua inteiro.
            </p>
          ) : replying ? (
            <form className="cx-mail-reply" onSubmit={send}>
              <label className="cx-sr-only" htmlFor="cx-mail-reply-body">
                Resposta
              </label>
              <textarea
                id="cx-mail-reply-body"
                value={body}
                onChange={(event) => setBody(event.target.value)}
                rows={6}
                placeholder="Escreva a resposta…"
              />
              <div className="cx-mail-reply-actions">
                <Button type="submit" variant="primary" icon={<Send aria-hidden="true" />} loading={busy}>
                  Enviar
                </Button>
                <Button type="button" variant="ghost" onClick={() => void saveDraft()} disabled={busy}>
                  Salvar rascunho
                </Button>
                <Button type="button" variant="text" onClick={() => setReplying(false)} disabled={busy}>
                  Cancelar
                </Button>
                <span className="cx-mail-hint">
                  O envio entra na fila local e nasce como demonstração: nada sai deste ambiente.
                </span>
              </div>
            </form>
          ) : (
            <div className="cx-mail-reply-start">
              <Button variant="primary" icon={<Reply aria-hidden="true" />} onClick={() => setReplying(true)}>
                Responder
              </Button>
              <Button
                variant="ghost"
                className="cx-mail-suggest"
                icon={
                  <span className="cx-mail-spark" aria-hidden="true">
                    <CosmosAiIcon size={14} />
                  </span>
                }
                loading={sugerindo}
                onClick={async () => {
                  const ultima = [...letters]
                    .reverse()
                    .find((item) => item.direction === "inbound");
                  const recuo = () =>
                    setSuggestion({
                      origem: "local",
                      text: localSuggestion(lead?.name, ultima?.bodyText),
                    });
                  if (!ultima) {
                    recuo();
                    return;
                  }
                  setSugerindo(true);
                  const result = await store.requestAiSuggestion({
                    threadId: thread.id,
                    messageId: ultima.id,
                  });
                  setSugerindo(false);
                  if (!result.ok) {
                    // O comando já avisou o motivo; a redação determinística
                    // continua servindo, e a tela diz que foi ela.
                    recuo();
                    return;
                  }
                  const { output } = result.value.result;
                  setSuggestion({
                    origem: "pipeline",
                    text: output.suggestedReply,
                    summary: output.summary,
                    requestId: result.value.request.id,
                    resultId: result.value.result.id,
                    review: output.needsHumanReview,
                  });
                }}
              >
                Sugerir resposta
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Uma carta da conversa, como no Gmail e no protótipo: só a última chega
 * aberta; as anteriores mostram o remetente e uma linha do corpo, e abrem pelo
 * cabeçalho. O endereço e os detalhes ficam atrás de "para mim".
 */
function Letter({
  message,
  thread,
  open,
}: {
  message: CommunicationMessage;
  thread: CommunicationThread;
  open: boolean;
}) {
  const store = useStore();
  const lead = store.leads.find((item) => item.id === thread.leadId);
  const outbound = message.direction === "outbound";
  const who = outbound ? (message.fromName ?? "COSMOS Comercial") : (lead?.name ?? message.fromName ?? message.fromAddress);
  const collapsed = message.bodyText.replace(/\s+/g, " ").trim();

  return (
    <details className="cx-mail-letter" open={open}>
      <summary>
        <Avatar name={who} />
        <span className="cx-mail-letter-person">
          <strong>{who}</strong>
          <small className="cx-mail-letter-email">{message.fromAddress || "Endereço não informado"}</small>
          <span className="cx-mail-collapsed">{collapsed}</span>
        </span>
        <time dateTime={message.occurredAt}>{mailFullStamp(message.occurredAt)}</time>
        <ChevronDown className="cx-mail-letter-chevron" size={14} aria-hidden="true" />
      </summary>
      <details className="cx-mail-meta">
        <summary aria-label="Mostrar detalhes do e-mail">
          para {outbound ? (lead?.name ?? message.toAddresses.join(", ")) : "mim"}
          <ChevronDown size={11} aria-hidden="true" />
        </summary>
        <dl>
          <dt>de:</dt>
          <dd>
            {who}
            {message.fromAddress ? ` <${message.fromAddress}>` : ""}
          </dd>
          <dt>para:</dt>
          <dd>{message.toAddresses.join(", ")}</dd>
          <dt>data:</dt>
          <dd>{mailFullStamp(message.occurredAt)}</dd>
          <dt>assunto:</dt>
          <dd>{message.subject || thread.subject}</dd>
        </dl>
      </details>
      <div className="cx-mail-body">{message.bodyText}</div>
    </details>
  );
}

/** Novo e-mail: escolhe o cadastro, escreve e guarda o rascunho. */
function ComposeDialog({ onClose }: { onClose: () => void }) {
  const store = useStore();
  const [leadId, setLeadId] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mailbox = (store.mailboxes ?? []).find((box) => box.channel === "email");
  const options = store.leads
    .filter((lead) => Boolean(lead.email) && !lead.archivedAt)
    .map((lead) => ({ value: lead.id, label: `${lead.name} · ${lead.company}` }));
  const lead = store.leads.find((item) => item.id === leadId);
  const ready = Boolean(mailbox && lead?.email && subject.trim() && body.trim());

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready || !mailbox || !lead?.email || busy) return;
    setBusy(true);
    try {
      const draft = await store.saveEmailDraft({
        mailboxId: mailbox.id,
        to: [lead.email],
        subject: subject.trim(),
        bodyText: body.trim(),
      });
      if (!draft.ok) {
        setError(draft.error.message);
        return;
      }
      const queued = await store.queueEmailSend(draft.value.message.id);
      if (!queued.ok) {
        setError(queued.error.message);
        return;
      }
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Novo e-mail"
      description="O envio fica registrado como demonstração local; nada sai deste ambiente."
      dirty={Boolean(subject || body)}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="cx-mail-compose"
            variant="primary"
            icon={<Send aria-hidden="true" />}
            loading={busy}
            disabled={!ready}
          >
            Enviar
          </Button>
        </>
      }
    >
      <form id="cx-mail-compose" className="cx-mail-compose" onSubmit={submit}>
        <Field label="Para" required>
          {({ id }) => (
            <Select
              options={options}
              value={leadId}
              onChange={setLeadId}
              labelledBy={id}
              size="field"
              placeholder="Escolha o cadastro"
            />
          )}
        </Field>
        <Field label="Assunto" required>
          {({ id }) => (
            <TextInput id={id} value={subject} onChange={(event) => setSubject(event.target.value)} />
          )}
        </Field>
        <Field label="Mensagem" required>
          {({ id }) => (
            <textarea id={id} value={body} onChange={(event) => setBody(event.target.value)} rows={8} />
          )}
        </Field>
        {!mailbox && (
          <p className="cx-mail-notice" role="status">
            Nenhuma caixa de e-mail configurada nesta amostra.
          </p>
        )}
        {error && (
          <p className="cx-mail-notice" role="alert">
            {error}
          </p>
        )}
      </form>
    </Dialog>
  );
}
